"use client";

import { useState, useEffect, useRef } from "react";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { isSalesCountryLocale } from "@/config/locale";
import { getRuntimeExtraCopy } from "@/lib/locale/runtime-extra/copy";
import sourceDe from "@/lib/locale/runtime-extra/source.de.json";

const IN_APP_BROWSERS = [
	/GSA\//i,
	/FBAN|FBAV/i,
	/Instagram/i,
	/musical_ly|Bytedance|TikTok/i,
	/Snapchat/i,
	/Twitter|X-app/i,
	/LinkedIn/i,
];

const TRANSLATIONS: Record<string, { message: string; cta: string }> = {
	de: {
		message: sourceDe["browser.message"],
		cta: sourceDe["browser.cta"],
	},
	en: {
		message: "For the best checkout experience, we recommend your default browser.",
		cta: "Open in browser",
	},
	it: {
		message: "Per un'esperienza di checkout ottimale, ti consigliamo il tuo browser predefinito.",
		cta: "Apri nel browser",
	},
	es: {
		message: "Para una mejor experiencia de pago, te recomendamos tu navegador predeterminado.",
		cta: "Abrir en navegador",
	},
	fr: {
		message: "Pour une expérience de paiement optimale, nous vous recommandons votre navigateur par défaut.",
		cta: "Ouvrir dans le navigateur",
	},
	nl: {
		message: "Voor de beste checkout-ervaring raden wij je standaardbrowser aan.",
		cta: "Openen in browser",
	},
	pt: {
		message: "Para uma melhor experiência de checkout, recomendamos o seu navegador padrão.",
		cta: "Abrir no navegador",
	},
	pl: {
		message: "Dla najlepszego doświadczenia zakupowego, zalecamy domyślną przeglądarkę.",
		cta: "Otwórz w przeglądarce",
	},
};

export function InAppBrowserBanner({ locale = "en" }: { locale?: string }) {
	const [show, setShow] = useState(false);
	const initializedRef = useRef(false);

	useEffect(() => {
		if (initializedRef.current) return;
		initializedRef.current = true;

		const dismissed = document.cookie.includes("dismiss_inapp_banner=1");
		if (dismissed) return;

		const ua = navigator.userAgent;
		const isInApp = IN_APP_BROWSERS.some((re) => re.test(ua));
		if (isInApp) {
			setShow(true);
		}
	}, []);

	if (!show) {
		return null;
	}

	const extra = isSalesCountryLocale(locale) ? getRuntimeExtraCopy(locale) : null;
	const t = extra
		? { message: extra["browser.message"], cta: extra["browser.cta"] }
		: (TRANSLATIONS[locale] ?? TRANSLATIONS.en);

	const handleOpenExternal = () => {
		// Best-effort: _blank works in GSA, may not work in all in-app browsers
		window.open(window.location.href, "_blank");
	};

	const handleDismiss = () => {
		document.cookie = "dismiss_inapp_banner=1;path=/;max-age=86400";
		setShow(false);
	};

	return (
		<div className="mx-auto mb-4 flex max-w-lg items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
			<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
			<div className="flex-1">
				<p>{t.message}</p>
				<button
					onClick={handleOpenExternal}
					className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700"
				>
					<ExternalLink className="h-3.5 w-3.5" />
					{t.cta}
				</button>
			</div>
			<button onClick={handleDismiss} className="shrink-0 text-amber-600 hover:text-amber-800">
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
